import { z } from 'zod'
import { salesChannels } from './shopware.constants'

export const SalesChannelSchema = z.enum(salesChannels)

export const LoginResponseSchema = z.object({
  contextToken: z.string(),
  salesChannel: SalesChannelSchema,
  countryCode: z.string(),
  currencyId: z.string(),
  expiresAt: z.string(),
})

export const UserSchema = z.object({
  createdAt: z.string(),
  updatedAt: z.string(),
  groupId: z.string(),
  defaultPaymentMethodId: z.string(),
  salesChannelId: z.string(),
  languageId: z.string(),
  lastPaymentMethodId: z.string().nullable(),
  defaultBillingAddressId: z.string(),
  defaultShippingAddressId: z.string(),
  defaultShippingAddress: z.object({
    countryId: z.string(),
  }),
  customerNumber: z.string(),
  salutationId: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  title: z.string().nullable(),
  active: z.boolean(),
  id: z.string(),
  birthday: z.string().nullable(),
  customFields: z
    .object({
      sap_id: z.string().nullish(),
      profile_picture_url: z.string().url().nullish(),
      biography: z.string().nullish(),
      preferred_language: z.string().nullish(),
      interests: z.string().nullish(),
      customer_newsletter: z.boolean().nullish(),
      newsletter: z.boolean().nullish(),
      assign_customers: z.boolean().nullish(),
      assign_new_client: z.boolean().nullish(),
      assign_new_partner: z.boolean().nullish(),
      data_privacy: z.boolean().nullish(),
      is_customer_shop_accepted: z.boolean().nullish(),
      customer_representative: z.string().nullish(),
      customer_representative_name: z.string().nullish(),
      algorithm_representatives: z.string().nullish(),
      algorithm_redirected: z.boolean().nullish(),
      customer_how_heard: z.string().nullish(),
      club_level: z.string().nullish(),
      tax_number: z.string().nullish(),
      ust_idnr: z.string().nullish(),
      former_sales_partner: z.boolean().nullish(),
      zahlfcode: z.number().nullish(),
    })
    .nullable(),
})

export const ShopwareErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    status: z.string(),
    title: z.string(),
    detail: z.string(),
  }),
})

export const SalutationSchema = z.object({
  translated: z.object({
    displayName: z.string(),
  }),
  displayName: z.string(),
  salutationKey: z.string(),
  id: z.string(),
})

export const SalutationsPayloadSchema = z.object({
  elements: z.array(SalutationSchema),
})

export const CountryStateSchema = z.object({
  id: z.string(),
  name: z.string(),
  shortCode: z.string(),
  translated: z
    .object({
      name: z.string(),
    })
    .optional(),
})

export const CountrySchema = z.object({
  translated: z.object({
    name: z.string(),
  }),
  states: z.array(CountryStateSchema).nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  name: z.string(),
  iso: z.string(),
  position: z.number(),
  active: z.boolean(),
  shippingAvailable: z.boolean(),
  iso3: z.string(),
  // displayStateInRegistration: false,
  // forceStateInRegistration: false,
  // checkVatIdPattern: false,
  // vatIdPattern: '(DE)?[0-9]{9}',
  // vatIdRequired: false,
  // customerTax: {},
  // companyTax: {},
  // states: null,
  // translations: null,
  // postalCodeRequired: false,
  // checkPostalCodePattern: false,
  // checkAdvancedPostalCodePattern: false,
  // advancedPostalCodePattern: null,
  // defaultPostalCodePattern: '\\d{5}',
  id: z.string(),
})

export const CountriesPayloadSchema = z.object({
  elements: z.array(CountrySchema),
})

export const PaymentMethodSchema = z.object({
  elements: z.array(
    z.object({
      translated: z.object({
        name: z.string(),
        distinguishableName: z.string(),
        description: z.string().nullable(),
        // customFields: {
        //   worldline_saferpay_payment_means: ['TWINT'],
        //   worldline_saferpay_wallets: [],
        //   worldline_saferpay_liability_shift_behavior: 'ignore',
        // },
      }),
      createdAt: z.string(),
      updatedAt: z.string().nullable(),
      name: z.string(),
      distinguishableName: z.string(),
      description: z.string().nullable(),
      position: z.number(),
      active: z.boolean(),
      afterOrderEnabled: z.boolean(),
      // translations: null,
      // mediaId: null,
      // media: null,
      shortName: z.string(),
      technicalName: z.string().nullish(),
      // synchronous: false,
      // asynchronous: true,
      // prepared: false,
      // refundable: true,
      // recurring: false,
      // customFields: {
      //   worldline_saferpay_payment_means: ['TWINT'],
      //   worldline_saferpay_wallets: [],
      //   worldline_saferpay_liability_shift_behavior: 'ignore',
      // },
      id: z.string(),
      // apiAlias: 'payment_method',
    }),
  ),
})

export type PaymentMethodsData = z.infer<typeof PaymentMethodSchema>

export const ShippingMethodSchema = z.object({
  elements: z.array(
    z.object({
      translated: z.object({
        name: z.string(),
        // customFields: [],
        description: z.string().nullable(),
        trackingUrl: z.string().nullable(),
      }),
      createdAt: z.string(),
      updatedAt: z.string().nullable(),
      active: z.boolean(),
      position: z.number(),
      description: z.string().nullable(),
      trackingUrl: z.string().nullable(),
      deliveryTimeId: z.string(),
      deliveryTime: z.object({
        translated: z.object({
          name: z.string(),
          // customFields: [],
        }),
        createdAt: z.string(),
        // updatedAt: null,
        name: z.string(),
        min: z.number(),
        max: z.number(),
        unit: z.string(),
        // customFields: null,
        id: z.string(),
        // apiAlias: 'delivery_time',
      }),
      // translations: null,
      // availabilityRule: null,
      // prices: [],
      // mediaId: null,
      // media: null,
      // tags: null,
      // taxType: 'auto',
      technicalName: z.string().nullable().optional(),
      // tax: null,
      // customFields: null,
      id: z.string(),
      // apiAlias: 'shipping_method',
    }),
  ),
})
